import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined"
import LightBulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import MultipleStopOutlinedIcon from "@mui/icons-material/MultipleStopOutlined"
import PagesOutlinedIcon from "@mui/icons-material/PagesOutlined"
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined"
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"

import { isConcierge, isSmsOnly } from "../../users/accountRole"
import { TrialFinishedRoadblockDialogData } from "./trialRoadblockData"

export class SidebarLinkData {
  constructor(
    title,
    url,
    icon,
    accessibleWithoutSubscription: Boolean,
    subscribeReason: String,
    options = {}
  ) {
    this.title = title
    this.url = url
    this.icon = icon
    this.accessibleWithoutSubscription = accessibleWithoutSubscription
    this.subscribeReason = subscribeReason
    // Which live count (if any) the sidebar shows next to the link.
    this.badgeKey = options.badgeKey || null
    this.hiddenForConcierge = Boolean(options.hiddenForConcierge)
    this.conciergeOnly = Boolean(options.conciergeOnly)
  }

  isVisibleFor(profile) {
    const concierge = isConcierge(profile)
    if (this.hiddenForConcierge && concierge) return false
    if (this.conciergeOnly && !concierge) return false
    return true
  }

  getLinkProps() {
    if (this.accessibleWithoutSubscription) {
      return {}
    }
    return new TrialFinishedRoadblockDialogData(
      this.title,
      this.subscribeReason
    ).getElementProps()
  }
}

export const FREE_SIDEBAR_LINKS = {
  candidates: new SidebarLinkData(
    "Candidates",
    "/candidates",
    PersonOutlinedIcon,
    true
  ),
  // The inbox used to live under Settings (always free); keep it reachable.
  messages: new SidebarLinkData(
    "Messages",
    "/messages",
    InboxOutlinedIcon,
    true,
    undefined,
    // The inbox APIs are concierge-only (IsConciergeUser) today.
    { badgeKey: "messages", conciergeOnly: true }
  ),
  strategyCenter: new SidebarLinkData(
    "Strategy Center",
    "/strategy-center/resources",
    LightBulbOutlinedIcon,
    true,
    undefined,
    { hiddenForConcierge: true }
  ),
  settings: new SidebarLinkData(
    "Settings",
    "/settings",
    SettingsOutlinedIcon,
    true
  ),
}

export const SIDEBAR_LINK_GROUPS = [
  {
    label: "Today",
    links: [
      new SidebarLinkData(
        "Activity",
        "/activity",
        HistoryOutlinedIcon,
        false,
        "Subscribe to view account activity and download your reports"
      ),
      FREE_SIDEBAR_LINKS.messages,
      // No badge: /crm/api/tasks/ has no open/due count to read cheaply.
      new SidebarLinkData(
        "Tasks",
        "/tasks",
        AssignmentOutlinedIcon,
        false,
        "Subscribe to create and manage tasks"
      ),
    ],
  },
  {
    label: "Recruiting",
    links: [
      new SidebarLinkData(
        "My Leads",
        "/leads",
        PersonAddOutlinedIcon,
        false,
        "Subscribe to access your agents in My Leads page"
      ),
      FREE_SIDEBAR_LINKS.candidates,
      new SidebarLinkData(
        "Pipeline",
        "/pipeline",
        MultipleStopOutlinedIcon,
        false,
        "Subscribe to organize your candidates into pipeline stages"
      ),
      new SidebarLinkData(
        "Proposals",
        "/proposals",
        PagesOutlinedIcon,
        false,
        "Subscribe to send proposals to your candidates",
        { hiddenForConcierge: true }
      ),
    ],
  },
  {
    label: "Outreach",
    links: [
      new SidebarLinkData(
        "Templates",
        "/templates",
        DashboardCustomizeOutlinedIcon,
        false,
        "Subscribe to create email and SMS templates for your candidates"
      ),
      new SidebarLinkData(
        "Workflows",
        "/workflows",
        AccountTreeOutlinedIcon,
        false,
        "Subscribe to create workflows and automate communication with your candidates",
        { hiddenForConcierge: true }
      ),
    ],
  },
]

export const SIDEBAR_BOTTOM_LINKS = [
  FREE_SIDEBAR_LINKS.strategyCenter,
  FREE_SIDEBAR_LINKS.settings,
]

/** Sidebar groups this user sees; groups left empty by role are dropped. */
export function getSidebarLinkGroups(profile) {
  return SIDEBAR_LINK_GROUPS.map((group) => ({
    ...group,
    links: group.links.filter((link) => link.isVisibleFor(profile)),
  })).filter((group) => group.links.length > 0)
}

export function getSidebarBottomLinks(profile) {
  return SIDEBAR_BOTTOM_LINKS.filter((link) => link.isVisibleFor(profile))
}

/**
 * Where "/" (and a login without ?next=) lands. Trial-ended accounts only
 * have the free pages, so they go straight to Candidates.
 */
export function getLandingPath(profile, subscription) {
  if (isSmsOnly(profile)) return "/texts"
  if (subscription?.trialFinished) return "/candidates"
  if (isConcierge(profile)) return "/leads"
  return "/activity"
}
